import React, { useState, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import Webcam from "react-webcam";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { 
  Camera, 
  Upload, 
  Image as ImageIcon, 
  Crop,
  RotateCcw,
  Download,
  X,
  Check
} from "lucide-react";
import { PHOTO_GUIDELINES } from "../../constants/businessCardStandards";

const PhotoUploader = ({ onPhotoSelect, existingPhoto, onRemovePhoto }) => {
  const { toast } = useToast();
  const [showCamera, setShowCamera] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cropData, setCropData] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user"
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!PHOTO_GUIDELINES.allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please select a JPEG, PNG, or WebP image",
        variant: "destructive"
      });
      return;
    }

    // Validate file size
    if (file.size > PHOTO_GUIDELINES.maxFileSize) {
      toast({
        title: "File too large",
        description: `Please select an image smaller than ${PHOTO_GUIDELINES.maxFileSize / (1024 * 1024)}MB`,
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setCapturedImage(e.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setShowCamera(false);
    setShowCropper(true);
  }, [webcamRef]);

  const getCroppedImage = useCallback((image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return canvas.toDataURL('image/jpeg', PHOTO_GUIDELINES.quality);
  }, []);

  const handleCropComplete = () => {
    if (!imageRef.current || !crop.width || !crop.height) {
      toast({
        title: "Invalid crop",
        description: "Please select a valid crop area",
        variant: "destructive"
      });
      return;
    }

    const croppedImageUrl = getCroppedImage(imageRef.current, crop);
    if (croppedImageUrl) {
      onPhotoSelect(croppedImageUrl);
      setShowCropper(false);
      setCapturedImage(null);
      toast({
        title: "Photo added",
        description: "Your photo has been added to the business card design"
      });
    }
  };

  const handleCancel = () => {
    setShowCamera(false);
    setShowCropper(false);
    setCapturedImage(null);
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ImageIcon className="h-5 w-5 text-purple-600" />
            <span>Add Photo to Business Card</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {existingPhoto ? (
            <div className="space-y-4">
              <div className="relative">
                <img 
                  src={existingPhoto} 
                  alt="Selected photo" 
                  className="w-32 h-32 object-cover rounded-lg border mx-auto"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                  onClick={onRemovePhoto}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 text-center">
                Photo added to your business card design
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center mb-4">
                Add a professional photo to your business card design
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="flex flex-col items-center space-y-2 h-24"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-6 w-6 text-blue-600" />
                  <span className="text-sm">Upload Photo</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="flex flex-col items-center space-y-2 h-24"
                  onClick={() => setShowCamera(true)}
                >
                  <Camera className="h-6 w-6 text-green-600" />
                  <span className="text-sm">Take Photo</span>
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Photo Guidelines:</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-1 space-y-1">
                  <li>• High-resolution photos (400x400px or larger)</li>
                  <li>• Professional headshot recommended</li>
                  <li>• Good lighting and clear background</li>
                  <li>• JPEG, PNG, or WebP format</li>
                  <li>• Maximum file size: 10MB</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={setShowCamera}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Take Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Webcam
                audio={false}
                height={480}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                width={640}
                videoConstraints={videoConstraints}
                className="rounded-lg"
              />
            </div>
            <div className="flex justify-center space-x-4">
              <Button onClick={capturePhoto} className="bg-green-600 hover:bg-green-700">
                <Camera className="h-4 w-4 mr-2" />
                Capture Photo
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper Dialog */}
      <Dialog open={showCropper} onOpenChange={setShowCropper}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Crop className="h-5 w-5" />
              <span>Crop Your Photo</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {capturedImage && (
              <div className="max-h-96 overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={1}
                  minWidth={100}
                  minHeight={100}
                >
                  <img
                    ref={imageRef}
                    src={capturedImage}
                    alt="Crop preview"
                    style={{ maxWidth: '100%' }}
                  />
                </ReactCrop>
              </div>
            )}
            
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-sm text-yellow-800">
                Adjust the crop area to select the portion of your photo you want to use. 
                A square crop works best for business cards.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleCropComplete} className="bg-purple-600 hover:bg-purple-700">
                <Check className="h-4 w-4 mr-2" />
                Use This Photo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoUploader;